const API_BASE_KEY = "intranet.apiBase";
const TENANT_KEY = "intranet.tenantId";
const WORKSPACE_KEY = "intranet.workspaceId";
// The login itself is the server's session cookie (HttpOnly, sent by the browser with every call).
// This key only says "this browser signed in", so the app can show the right screen before the first
// call answers; the server stays the judge, and its 401 ends the session here. The browser never
// holds the password (W06-F01): any script on the page or a shared computer could read it.
const SIGNED_IN_KEY = "intranet.signedIn";
// Older builds kept the username and password here. Removed on load, once, for everyone.
const LEGACY_PASSWORD_KEY = "intranet.basicAuth";

const DEFAULT_API_BASE = "/api";
export const PUBLIC_BASE_URL = window.location.origin;

console.log("API Client Initialized With Base:", DEFAULT_API_BASE);

//
if (localStorage.getItem(API_BASE_KEY)) {
  const current = localStorage.getItem(API_BASE_KEY);
  if (current.startsWith("http")) {
    console.warn("Removing Old Absolute API Base From Storage:", current);
    localStorage.removeItem(API_BASE_KEY);
  }
}
if (localStorage.getItem(LEGACY_PASSWORD_KEY) !== null) {
  localStorage.removeItem(LEGACY_PASSWORD_KEY);
  // The old entry was also how the app knew it was signed in; the cookie still is, so stay signed in.
  localStorage.setItem(SIGNED_IN_KEY, "1");
}

export function getApiSettings() {
  return {
    apiBase: localStorage.getItem(API_BASE_KEY) || DEFAULT_API_BASE,
    tenantId: localStorage.getItem(TENANT_KEY) || "1",
    workspaceId: localStorage.getItem(WORKSPACE_KEY) || "1",
    signedIn: localStorage.getItem(SIGNED_IN_KEY) === "1",
  };
}

export function saveApiSettings(settings) {
  if (settings.apiBase) localStorage.setItem(API_BASE_KEY, settings.apiBase);
  if (settings.tenantId) localStorage.setItem(TENANT_KEY, String(settings.tenantId));
  if (settings.workspaceId) localStorage.setItem(WORKSPACE_KEY, String(settings.workspaceId));
  if (settings.signedIn) localStorage.setItem(SIGNED_IN_KEY, "1");
}

// The operator back in their own company, after leaving or losing a support session (Entry 20
// review). Removing the stored id is not enough: every call then sends the "1" default, which
// names whichever company has id 1, and the screen can go on naming the company just left.
export function returnToOwnCompany(ownCompanyId) {
  if (ownCompanyId) localStorage.setItem(TENANT_KEY, String(ownCompanyId));
  else localStorage.removeItem(TENANT_KEY);
}

// Forgets the signed-in mark AND the company and workspace the last person used, so the next
// sign-in on this browser starts clean like a new browser would.
export function clearApiAuth() {
  localStorage.removeItem(SIGNED_IN_KEY);
  localStorage.removeItem(TENANT_KEY);
  localStorage.removeItem(WORKSPACE_KEY);
}

// Every way of ending a session (signing out, an expired session, being signed out in another
// tab) ends here: forget the login, then load the sign-in page afresh. A full load is the point:
// it drops everything the last person had in memory (pay, bank and HR records), which a
// client-side route change would leave behind for the next person to find.
export function endSession(goTo = (url) => window.location.assign(url)) {
  clearApiAuth();
  goTo("/login/");
}

export function makeUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const base = (localStorage.getItem(API_BASE_KEY) || DEFAULT_API_BASE).replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? match[1] : "";
}

function csrfHeaders() {
  const csrf = getCsrfToken();
  return csrf ? { "X-CSRFToken": csrf } : {};
}

// ─── Request Queue With Concurrency Limit (Prevents DB Connection Exhaustion) ───
const MAX_CONCURRENT_REQUESTS = parseInt(localStorage.getItem("intranet.maxConcurrentRequests") || "10", 10);
const REQUEST_QUEUE = [];
let ACTIVE_COUNT = 0;

function processQueue() {
  while (ACTIVE_COUNT < MAX_CONCURRENT_REQUESTS && REQUEST_QUEUE.length > 0) {
    const { resolve, reject, path, options } = REQUEST_QUEUE.shift();
    ACTIVE_COUNT++;
    executeRequest(path, options).then(resolve).catch(reject).finally(() => {
      ACTIVE_COUNT--;
      processQueue();
    });
  }
}

function enqueueRequest(path, options = {}) {
  return new Promise((resolve, reject) => {
    REQUEST_QUEUE.push({ resolve, reject, path, options });
    processQueue();
  });
}

async function executeRequest(path, options = {}) {
  const method = options.method || "GET";
  const body = options.body;
  const headers = new Headers(options.headers || {});
  const settings = getApiSettings();
  headers.set("X-Tenant-Id", String(settings.tenantId || "1"));
  headers.set("X-Workspace-Id", String(settings.workspaceId || "1"));
  headers.set("X-Request-Id", `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
  if (body !== undefined && !(body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (method !== "GET") {
    const csrf = getCsrfToken();
    if (csrf) headers.set("X-CSRFToken", csrf);
  }

  const controller = new AbortController();
  const timeoutMs = Number(options.timeoutMs || 120000);
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(makeUrl(path), {
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  const text = await response.text();
  const payload = text ? safeJson(text) : null;

  if (!response.ok) {
    const error = new Error(payload?.detail || payload?.message || `${response.status} ${response.statusText}`);
    error.status = authStatus(response.status, payload);
    error.payload = payload;
    // Treat only authentication failures as global unauthorized events.
    if (error.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized", { detail: { status: response.status, path } }));
    }
    throw error;
  }
  return payload;
}

// The backend authenticates by session only, and DRF answers a request with NO
// valid session with 403 "Authentication credentials were not provided." rather
// than 401. The app logs out only on 401 (so a real permission 403 never signs
// anybody out), which meant an expired or unknown session left the app stuck
// on "Connection problem" forever instead of going back to the login page.
// That one message is DRF's NotAuthenticated, i.e. a 401 in meaning, so it is
// reported as one. Every other 403 is a genuine permission refusal and is left
// alone.
const NOT_AUTHENTICATED_DETAIL = "Authentication credentials were not provided.";
function authStatus(status, payload) {
  if (status === 403 && payload?.detail === NOT_AUTHENTICATED_DETAIL) return 401;
  return status;
}

export async function apiRequest(path, options = {}) {
  // 
  const method = (options.method || "GET").toUpperCase();
  if (method === "GET") {
    return enqueueRequest(path, options);
  }
  return executeRequest(path, options);
}

export function apiGet(path) {
  return apiRequest(path);
}

// A file behind a signed-in endpoint (e.g. a candidate's ID document), as a
// Blob. A plain <a href> cannot carry the company header or report a lost
// session, and files like these must never be reachable by a bare URL.
export async function apiGetBlob(path) {
  return (await fetchFile(path)).blob();
}

// Save a file from a signed-in endpoint, under the name the server gives it. A plain
// navigation (window.location.href) cannot carry the X-Tenant-Id header, so during a support
// session it fetched the operator's own company's file, not the company on screen (Entry 20 review).
export async function apiDownload(path, fallbackName = "download") {
  const response = await fetchFile(path);
  const named = /filename="?([^";]+)"?/i.exec(response.headers.get("Content-Disposition") || "");
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = named ? named[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

async function fetchFile(path) {
  const settings = getApiSettings();
  const headers = new Headers();
  headers.set("X-Tenant-Id", String(settings.tenantId || "1"));
  headers.set("X-Workspace-Id", String(settings.workspaceId || "1"));
  const response = await fetch(makeUrl(path), { method: "GET", headers, credentials: "include" });
  if (!response.ok) {
    const payload = safeJson(await response.text());
    const error = new Error(payload?.detail || payload?.message || `${response.status} ${response.statusText}`);
    error.status = authStatus(response.status, payload);
    error.payload = payload;
    if (error.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized", { detail: { status: response.status, path } }));
    }
    throw error;
  }
  return response;
}

// A plain GET with no 401 handling, for the few endpoints a person with no
// account is meant to reach. The normal client, on a 401, clears the session
// and bounces to the login screen — which is exactly the wrong behaviour for a
// candidate who has no session to lose.
// The public calls below never wait forever: a stalled connection ends in an Error a screen can show,
// instead of a spinner that never stops. A document upload is given longer to finish on a slow phone.
const PUBLIC_TIMEOUT_MS = 30000;
const PUBLIC_UPLOAD_TIMEOUT_MS = 120000;

async function publicFetch(path, init, timeoutMs = PUBLIC_TIMEOUT_MS) {
  const base = getApiSettings().apiBase || "/api";
  try {
    return await fetch(`${base}${path.startsWith("/") ? path : `/${path}`}`, { ...init, signal: AbortSignal.timeout(timeoutMs) });
  } catch {
    throw new Error("The connection was lost. Please check your internet and try again.");
  }
}

export async function apiGetPublic(path) {
  const response = await publicFetch(path, { method: "GET", headers: { Accept: "application/json" } });
  // A server fault or a rate limit is a temporary problem, not an answer about the link: it throws, so the
  // caller can offer a retry instead of telling a candidate their link is no good (triage FE-06-11).
  if (response.status >= 500 || response.status === 429) throw new Error(`The server could not answer (${response.status}).`);
  if (!response.ok) return null;
  return response.json();
}

export async function apiPostPublic(path, body) {
  const response = await publicFetch(path, {
    method: "POST",
    // Public calls carry no credentials of their own, but a browser that also holds a
    // staff session sends its cookie too, and the server then wants the CSRF header.
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrfHeaders() },
    body: JSON.stringify(body || {}),
  });
  try { return await response.json(); } catch { return null; }
}

// Multipart, so no Content-Type header — the browser sets the boundary itself.
export async function apiUploadPublic(path, formData) {
  const response = await publicFetch(path, {
    method: "POST",
    headers: { Accept: "application/json", ...csrfHeaders() },
    body: formData,
  }, PUBLIC_UPLOAD_TIMEOUT_MS);
  try { return await response.json(); } catch { return null; }
}

export function apiPost(path, body) {
  return apiRequest(path, { method: "POST", body });
}

export function apiPatch(path, body) {
  return apiRequest(path, { method: "PATCH", body });
}

export function apiDelete(path) {
  return apiRequest(path, { method: "DELETE" });
}

export function unpackList(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of ["results", "data", "items", "rows", "projects", "documents", "user_list", ...keys]) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.results)) return value.results;
  }
  return [];
}

export function asQuery(params) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const value = query.toString();
  return value ? `?${value}` : "";
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch (_error) {
    return { raw: text };
  }
}
